import { UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getOrderSettings } from 'views/order-feed/api/get-order-settings';
import { getOfdFeedPrograms } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/api/get-ofd-feed-programs';
import {
  getOrderAssortment,
  getRecommendationAssortment,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/api/get-orders';
import { OfdFeedProgram } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/feed-programs.type';
import { OrderAssortment } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/order-assortment.type';
import { RecommendationAssortment } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/recommendation-assortment.type';
import { useSelectedSite } from 'views/order-feed/features/my-supply-plan/hooks/use-selected-site';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';
import { OrderMaintenance } from 'views/order-feed/types/order-maintenance.type';
import { isValidRouteParam } from 'views/order-feed/utils/is-valid-route-param';
import { getSiteSettings } from 'views/site-settings/api/site-settings';
import { AscCertificationStatus } from 'views/site-settings/types/asc-certification-status.type';

type State = {
  orderAssortmentQuery: UseQueryResult<OrderAssortment, AxiosError<unknown, any>>;
  recommendationAssortmentQuery: UseQueryResult<RecommendationAssortment, AxiosError<unknown, any>>;
  orderSettingsQuery: UseQueryResult<OrderMaintenance, AxiosError<unknown, any>>;
  ofdFeedProgramsQuery: UseQueryResult<OfdFeedProgram[], AxiosError<unknown, any>>;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  isAscCompliant: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsError: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSuccess: React.Dispatch<React.SetStateAction<boolean>>;
};

const OrdersContext = React.createContext<State | undefined>(undefined);

type OrdersProviderProps = {
  children: React.ReactNode;
};

const OrdersProvider: React.FC<OrdersProviderProps> = ({ children }) => {
  const {
    account: { company },
  } = useAccountState();

  const { selectedSite } = useSelectedSite();
  const params = useParams<MySupplyPlanParams>();

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isAscCompliant, setIsAscCompliant] = useState(false);

  const orderAssortmentQuery = useGet(
    ['getOrderAssortment', company?.customerErpId, params.siteErpId],
    ({ queryKey }) => getOrderAssortment(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!company?.customerErpId && isValidRouteParam(params.siteErpId),
    }
  );

  const recommendationAssortmentQuery = useGet(
    ['getRecommendationAssortment', selectedSite?.globalSiteId],
    ({ queryKey }) => getRecommendationAssortment(String(queryKey[1])),
    {
      enabled: !!selectedSite,
    }
  );

  const { data: siteSettings, isError: isSiteSettingsError } = useGet(
    ['getSiteSettings', selectedSite?.globalSiteId],
    ({ queryKey }) => getSiteSettings(String(queryKey[1])),
    {
      enabled: !!selectedSite?.globalSiteId,
    }
  );

  useEffect(() => {
    setIsAscCompliant(!isSiteSettingsError && siteSettings?.ascStatus === AscCertificationStatus.AscCertified);
  }, [siteSettings, isSiteSettingsError]);

  const orderSettingsQuery = useGet(
    ['getOrderSettings', company?.marketId],
    (context) => getOrderSettings(Number(context.queryKey[1])),
    {
      enabled: company?.features?.hasOrderFeed && !!company?.marketId,
    }
  );

  const ofdFeedProgramsQuery = useGet(
    ['getOfdFeedPrograms', selectedSite?.globalSiteId],
    ({ queryKey }) => getOfdFeedPrograms(String(queryKey[1])),
    {
      enabled: !!selectedSite?.globalSiteId,
    }
  );

  return (
    <OrdersContext.Provider
      value={{
        orderAssortmentQuery,
        recommendationAssortmentQuery,
        isError,
        isLoading,
        isSuccess,
        isAscCompliant,
        orderSettingsQuery,
        ofdFeedProgramsQuery,
        setIsError,
        setIsLoading,
        setIsSuccess,
      }}>
      {children}
    </OrdersContext.Provider>
  );
};

function useOrdersState() {
  const context = useContext(OrdersContext);
  if (context === undefined) {
    throw new Error('useOrdersState must be used within a OrdersProvider');
  }
  return context;
}

export { OrdersProvider, useOrdersState };
